class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.loadRecommendations();
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  // The cart-drawer-recommendations element sits off-canvas (and, once open, can
  // still be scrolled out of view below the cart items), so its own
  // IntersectionObserver may never see it intersect the viewport. Force its load
  // here instead, whenever the drawer opens or its contents are re-rendered
  // (e.g. right after an AJAX add-to-cart), using whatever fresh instance is
  // currently in the DOM.
  loadRecommendations() {
    const recommendations = this.querySelector('product-recommendations.cart-drawer-recommendations');
    if (recommendations) recommendations.loadRecommendations(recommendations.dataset.productId);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
       updateFreeShippingBar();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Collection-swatch links (rendered by snippets/collection-swatch.liquid) normally
// navigate to that variant's product page. Inside the cart-drawer recommendations,
// we want a click to just re-point that card's own "Add to cart" button at the
// picked variant instead of leaving the drawer. Delegated on document since the
// recommendations markup gets re-rendered (innerHTML swaps) after every fetch.
document.addEventListener('click', (event) => {
  const swatch = event.target.closest(
    '.cart-drawer-recommendations .collection-swatch-ctm a.swatch-input__label'
  );
  if (!swatch) return;

  event.preventDefault();

  const variantId = swatch.dataset.variantId;
  const card = swatch.closest('.card-wrapper');
  if (!variantId || !card) return;

  // Only clear the previous selection within this same option group (e.g. color),
  // not the whole card — otherwise picking a color would also un-select the size.
  const optionGroup = swatch.closest('.collection_swatches') || card;
  optionGroup.querySelectorAll('.swatch-input__label.is-selected').forEach((el) => el.classList.remove('is-selected'));
  swatch.classList.add('is-selected');

  const variantInput = card.querySelector('.product-variant-id');
  if (variantInput) variantInput.value = variantId;

  const submitButton = card.querySelector('.quick-add__submit');
  if (!submitButton) return;

  const isAvailable = swatch.dataset.available !== 'false';
  submitButton.disabled = !isAvailable;
  const label = submitButton.querySelector('span');
  if (label && window.variantStrings) {
    label.textContent = isAvailable ? window.variantStrings.addToCart : window.variantStrings.soldOut;
  }
});

function updateFreeShippingBar() {

  const drawer = document.querySelector("cart-drawer");
  if (!drawer) return;

  const wrapper = drawer.querySelector(".cart-progress-wrapper");
  if (!wrapper) return;

  const baseThreshold = parseFloat(wrapper.dataset.baseThreshold);
  const rate = window.Shopify?.currency?.rate || 1;

  fetch('/cart.js')
    .then(res => res.json())
    .then(cart => {

      const cartTotal = cart.total_price / 100;
      const convertedThreshold = baseThreshold * rate;

      const remaining = convertedThreshold - cartTotal;
      const progress = Math.min((cartTotal / convertedThreshold) * 100, 100);

      const fill = wrapper.querySelector(".cart-progress-fill");
      const text = wrapper.querySelector(".cart-progress-text");

      if (fill) fill.style.width = progress + "%";

      if (remaining > 0) {

        const formattedRemaining = new Intl.NumberFormat(Shopify.locale, {
          style: "currency",
          currency: Shopify.currency.active
        }).format(remaining);

        text.innerHTML = `You're <strong>${formattedRemaining}</strong> away from FREE shipping!`;

      } else {

        text.innerHTML = "🎉 You’ve unlocked FREE shipping!";

      }

    });
}


document.addEventListener("DOMContentLoaded", function () {

  updateFreeShippingBar();

  const drawer = document.querySelector("cart-drawer");
  if (!drawer) return;

  const observer = new MutationObserver(function () {
    updateFreeShippingBar();
  });

  observer.observe(drawer, {
    childList: true,
    subtree: true
  });

});